import Config from "@/common/config";
import {
  baseAxiosInstance,
  compassBaseAxiosInstance,
  itrBaseAxiosInstance,
} from "@/api/base";
import type { AxiosResponse } from "axios";
import {
  fillURLPlaceholder,
  fillMultipleURLPlaceholders,
  toRelative,
} from "@/common/helpers";
import type { EmailType, User } from "@/common/types/user";

const usersEndpoint = Config.apiEndpoints.users;

const allUsersEndpoint = toRelative(usersEndpoint);
const userEndpoint = toRelative(usersEndpoint + "/{id}");
const impersonateEndpoint = toRelative(usersEndpoint + "/{id}/impersonation");

const sendEmailEndpoint = toRelative(usersEndpoint + "/{id}/send-email");
const defaultEndpoint = Config.apiEndpoints.default;
const itrSettingsEndpoint = Config.apiEndpoints.copyItrUserSettings;
const resetUserInfoEndpoint = toRelative(defaultEndpoint + "/reset-user-info");
const erpUsersEndpoint = "/compass/api/users/erp";
const itrCopyUserSettingsEndpoint = toRelative(
  itrSettingsEndpoint + "/{sourceUserId}/copy/{targetUserId}",
);

/**
 * @summary Retrieves all available users
 */
async function getAllUsers(): Promise<AxiosResponse> {
  return await baseAxiosInstance.get(allUsersEndpoint);
}

/**
 * @summary Creates a new user
 * @param user The user to create.
 */
async function postNewUser(user: User): Promise<AxiosResponse> {
  return await baseAxiosInstance.post(usersEndpoint, user);
}

/**
 * @summary Retrieves the user with the provided id
 * @param id The id of the user to retrieve.
 */
async function getUserByID(id: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.get(
    fillURLPlaceholder(userEndpoint, "id", id),
  );
}

/**
 * @summary Get visibility by username
 * @param userName the username
 */
async function getVisibilityForUser(userName: string): Promise<AxiosResponse> {
  const params = new URLSearchParams({
    username: userName,
    showVisibility: "true",
  });

  return await baseAxiosInstance.get(usersEndpoint, { params });
}

async function copyItrSettingsFromUser(
  sourceUserId: string,
  targetUserId: string,
): Promise<AxiosResponse> {
  return await itrBaseAxiosInstance.post(
    fillMultipleURLPlaceholders(itrCopyUserSettingsEndpoint, {
      sourceUserId: sourceUserId,
      targetUserId: targetUserId,
    }),
  );
}

/**
 * @summary Updates the user with the provided id
 * @param id The id of the user to update.
 * @param user The new user information.
 */
async function putUserByID(
  id: string,
  user: Partial<User>,
): Promise<AxiosResponse> {
  return await baseAxiosInstance.put(
    fillURLPlaceholder(userEndpoint, "id", id),
    user,
  );
}

/**
 * @summary Deletes the users with the provided id
 * @param id The id of the user to delete.
 */
async function deleteUserByID(id: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.delete(
    fillURLPlaceholder(userEndpoint, "id", id),
  );
}

/**
 * @summary Impersonate the user with the provided id
 * @param id The id of the user to impersonate.
 */
async function impersonateByID(id: string): Promise<AxiosResponse> {
  return await baseAxiosInstance.post(
    fillURLPlaceholder(impersonateEndpoint, "id", id),
  );
}

/**
 * @summary Sends the verification email to the user with the provided id
 * @param id The id of the user to whom the email should be sent.
 * @param emailType accountActivated, unknownCompany, newAddress or accountDeactivated
 */
async function sendEmail(
  id: string,
  emailType: EmailType,
): Promise<AxiosResponse> {
  return await baseAxiosInstance.put(
    fillURLPlaceholder(sendEmailEndpoint, "id", id),
    emailType,
  );
}

/**
 * Resets specific user information for all users
 * @param resetUserInfoType possible values are "terms", "privacy" and "password"
 */
async function resetUserInfo(
  resetUserInfoType: string,
): Promise<AxiosResponse> {
  return await baseAxiosInstance.put(resetUserInfoEndpoint, {
    type: resetUserInfoType,
  });
}

async function getErpUsers(): Promise<AxiosResponse> {
  return await compassBaseAxiosInstance.get(erpUsersEndpoint);
}

export const usersAPI = {
  getAllUsers,
  getErpUsers,
  getVisibilityForUser,
  postNewUser,
  getUserByID,
  putUserByID,
  deleteUserByID,
  impersonateByID,
  resetUserInfo,
  sendEmail,
  copyItrSettingsFromUser,
};
